import React, { FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SearchField, SMBSelect } from '../../../components';
import { getChecklistTypes } from '../../../fetch';
import { IChecklistType } from '../../../models';
import { MenuItem, Grid, Button, Divider } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';

interface IChecklistTemplateFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  selectedType: string | number;
  setSelectedType: (val: string) => void;
  setSearchValue: (val: string) => void;
  setSearchedValue: (val: string) => void;
  searchValue: string;
  handleSearch: (val: string) => void;
  hasAppliedFilters: boolean;
  setHasAppliedFilters: (val: boolean) => void;
}

export const ChecklistTemplateFilters: FC<IChecklistTemplateFilters> = ({
  hasAppliedFilters,
  setHasAppliedFilters,
  isLoading,
  applyFilters,
  selectedType,
  setSelectedType,
  setSearchValue,
  setSearchedValue,
  handleSearch,
  searchValue
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const [isLoadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [types, setTypes] = useState<IChecklistType[]>([]);
  const classes = useStyles({ isMobileFilterButtonOpen });
  const fetchChecklistTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await getChecklistTypes();
      setTypes(res.records);
    } catch (error) {
    } finally {
      setLoadingTypes(false);
    }
  };

  useEffect(() => {
    fetchChecklistTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SearchField
            isDisabled={isLoading}
            handleSearch={handleSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SMBSelect
            variant="outlined"
            label="Type"
            value={selectedType}
            onChange={({ target: { value } }) => setSelectedType(value as string)}
            disabled={isLoading || isLoadingTypes}
            handleClear={() => {
              setSelectedType('');
            }}
          >
            {types.map((type, index) => {
              return (
                <MenuItem key={`${index}`} value={type.checklistTypeId}>
                  {type.name}
                </MenuItem>
              );
            })}
          </SMBSelect>
        </Grid>

        <Grid item xs={12} md={5}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setSearchedValue(searchValue);
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              color="inherit"
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSearchValue('');
                setSelectedType('');
                setSearchedValue('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
